import { useState, useEffect } from "react";
import { usePermissions } from "./PermissionsContext";

const storedName = sessionStorage.getItem("userName");
const storedEmail = sessionStorage.getItem("userEmail") || "";
const LOGGED_IN = {
  name: storedName && storedName !== storedEmail ? storedName : "",
  surname: sessionStorage.getItem("userSurname") || "",
  email: storedEmail,
  phone: sessionStorage.getItem("userPhone") || "",
  role: sessionStorage.getItem("userRole") || "User",
  theme: localStorage.getItem("theme") || "light",
  defaultView: "dashboard",
  twoFactor: false,
};

function Section({ title, children, icon }) {
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="ph">
        <i
          className={`bi ${icon}`}
          style={{ color: "var(--flame)", fontSize: 14 }}
        />
        <span className="ph-title">{title}</span>
      </div>
      <div style={{ padding: "18px 20px" }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "left",
        gap: 0,
        marginRight: 10,
        paddingBottom: 16,
        marginBottom: 16,
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fs12 fw6" style={{ marginBottom: 2 }}>
          {label}
        </div>
        {hint && <div className="fs10 tc3">{hint}</div>}
      </div>
      <div style={{ flexShrink: 0, minWidth: 220 }}>{children}</div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      className="fi"
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ fontSize: 12 }}
    />
  );
}

function PasswordInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <input
        className="fi"
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          fontSize: 12,
          paddingRight: 32,
          width: "100%",
          boxSizing: "border-box",
        }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        style={{
          position: "absolute",
          right: 8,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          color: "var(--text3)",
          display: "flex",
          alignItems: "center",
        }}
        tabIndex={-1}
        title={show ? "Hide password" : "Show password"}
      >
        <i
          className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`}
          style={{ fontSize: 13 }}
        />
      </button>
    </div>
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select
      className="fsel"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: 12 }}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.seritonex.com";

const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1] || "";
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const stored = sessionStorage.getItem("userId");
  if (stored) return stored;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("authToken");
  const claims = decodeJwt(token);
  return claims?.sub || claims?.user_id || claims?.id || "";
};

const fetchUserById = async (id) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("authToken");
  try {
    const res = await fetch(`${API_BASE}/api/v1/amis/user/by-id`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ user_id: String(id) }),
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data) return null;
    return {
      id: data.user_id ?? data.id ?? data.userId ?? "",
      name: data.name || data.first_name || data.given_name || "",
      surname: data.surname || data.last_name || data.family_name || "",
      email: data.email_address || data.email || "",
      phone: data.phone || data.phone_number || data.contact_number || "",
      role:
        data.group || data.role || sessionStorage.getItem("userRole") || "User",
    };
  } catch {
    return null;
  }
};

function AccountSettingsPage({ showToast: toast }) {
  const [form, setForm] = useState(() => {
    try {
      return {
        ...LOGGED_IN,
        theme: localStorage.getItem("theme") || LOGGED_IN.theme,
      };
    } catch {
      return LOGGED_IN;
    }
  });
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  // password match state — only show mismatch after user has typed in confirm
  const pwdMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;
  const pwdReady = password.length > 0 && password === confirmPassword;

  useEffect(() => {
    try {
      const root = document.documentElement;
      if (form.theme === "light") root.classList.add("light-theme");
      else root.classList.remove("light-theme");
      localStorage.setItem("theme", form.theme);
    } catch {
      /* noop */
    }
  }, [form.theme]);

  useEffect(() => {
    const loadUser = async () => {
      const userId = getCurrentUserId();
      if (!userId) return;
      const user = await fetchUserById(userId);
      if (!user) return;
      setForm((current) => ({
        ...current,
        name: user.name || current.name,
        surname: user.surname || current.surname,
        email: user.email || current.email,
        phone: user.phone || current.phone,
        role: user.role || current.role,
      }));
      sessionStorage.setItem("userId", String(user.id || userId));
      if (user.name) sessionStorage.setItem("userName", user.name);
      if (user.surname) sessionStorage.setItem("userSurname", user.surname);
      if (user.email) sessionStorage.setItem("userEmail", user.email);
      if (user.phone) sessionStorage.setItem("userPhone", user.phone);
      if (user.role) sessionStorage.setItem("userRole", user.role);
    };
    loadUser();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = getCurrentUserId();
      if (!userId) throw new Error("No user ID available");
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/v1/amis/user/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: String(userId),
          name: form.name,
          surname: form.surname,
          email_address: form.email,
          contact_number: form.phone,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const msg = body?.message || body?.error || `Status ${res.status}`;
        throw new Error(msg);
      }
      // sync session storage with saved values
      if (form.name) sessionStorage.setItem("userName", form.name);
      if (form.surname) sessionStorage.setItem("userSurname", form.surname);
      if (form.email) sessionStorage.setItem("userEmail", form.email);
      toast("Profile updated successfully", "s");
    } catch (err) {
      toast(err?.message || "Could not save profile", "e");
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    if (!pwdReady) return;
    setPwdSaving(true);
    try {
      const userId = getCurrentUserId();
      if (!userId) throw new Error("No user ID available");
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/v1/amis/user/update-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: String(userId),
          secret: String(password),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.error || `Status ${res.status}`);
      }
      toast("Password updated successfully", "s");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast(err?.message || "Could not update password", "e");
    } finally {
      setPwdSaving(false);
    }
  };

  const nav = [
    { id: "profile", icon: "bi-person-circle", label: "Profile" },
    { id: "security", icon: "bi-shield-lock", label: "Security" },
  ];

  const { canSeePage, ready } = usePermissions();
  if (ready && !canSeePage("account-settings")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">
          You do not have permission to edit account settings.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ marginBottom: 22 }}>
        <div className="syne fw7" style={{ fontSize: 20, marginBottom: 4 }}>
          Account Settings
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "200px 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        {/* ── left nav ── */}
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            overflow: "hidden",
            position: "sticky",
            top: 0,
          }}
        >
          <div
            style={{
              padding: "16px 16px 14px",
              borderBottom: "1px solid var(--border)",
              textAlign: "center",
            }}
          >
            <div className="fs16 fw7">{form.name}</div>
            <div className="fs10 tc3" style={{ marginTop: 2 }}>
              {form.role}
            </div>
          </div>

          {nav.map(({ id, icon, label }) => (
            <div
              key={id}
              onClick={() => setActiveSection(id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "10px 16px",
                cursor: "pointer",
                borderLeft: `2px solid ${activeSection === id ? "var(--flame)" : "transparent"}`,
                background:
                  activeSection === id ? "rgba(255,79,23,.07)" : "transparent",
                transition: "all .13s",
              }}
            >
              <i
                className={`bi ${icon}`}
                style={{
                  fontSize: 14,
                  color: activeSection === id ? "var(--flame)" : "var(--text3)",
                  width: 16,
                  textAlign: "center",
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: activeSection === id ? "var(--flame)" : "var(--text2)",
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* ── right content ── */}
        <div>
          {/* PROFILE */}
          {activeSection === "profile" && (
            <Section title="Profile Information" icon="bi-person-circle">
              <Field
                label="First Name"
                hint="Your display name across the system"
              >
                <TextInput value={form.name} onChange={set("name")} />
              </Field>
              <Field label="Surname" hint="Your family name">
                <TextInput value={form.surname} onChange={set("surname")} />
              </Field>
              <Field label="Email Address" hint="Your primary email for login">
                <TextInput
                  value={form.email}
                  onChange={set("email")}
                  type="email"
                />
              </Field>
              <Field
                label="Phone Number"
                hint="Your primary phone number for contacts"
              >
                <TextInput
                  value={form.phone}
                  onChange={set("phone")}
                  type="tel"
                />
              </Field>
            </Section>
          )}

          {/* SECURITY */}
          {activeSection === "security" && (
            <Section title="Security Settings" icon="bi-shield-lock">
              <Field
                label="Change Password"
                hint="Choose a new password for your account"
              >
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <PasswordInput
                    placeholder="New password"
                    value={password}
                    onChange={setPassword}
                  />
                  <PasswordInput
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                  />

                  {/* match indicator */}
                  {confirmPassword.length > 0 && (
                    <div
                      className="fs10"
                      style={{
                        color: pwdMismatch
                          ? "var(--danger, #e05)"
                          : "var(--success, #3c9)",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <i
                        className={`bi ${pwdMismatch ? "bi-x-circle" : "bi-check-circle"}`}
                      />
                      {pwdMismatch
                        ? "Passwords do not match"
                        : "Passwords match"}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="btn btn-d btn-sm"
                      onClick={() => {
                        setPassword("");
                        setConfirmPassword("");
                        toast("Password entry cleared", "w");
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-s btn-sm"
                      onClick={updatePassword}
                      disabled={pwdSaving || !pwdReady}
                      title={
                        !pwdReady
                          ? "Passwords must match before updating"
                          : undefined
                      }
                    >
                      {pwdSaving ? (
                        <>
                          <i className="bi bi-arrow-repeat spin" /> Updating…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-key" /> Update Password
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Field>
            </Section>
          )}

          {/* ── save bar (profile only) ── */}
          {activeSection === "profile" && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                paddingTop: 4,
              }}
            >
              <button
                className="btn btn-g"
                onClick={() => {
                  setForm(LOGGED_IN);
                  toast("Changes discarded", "w");
                }}
              >
                Discard
              </button>
              <button
                className="btn btn-p"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="bi bi-arrow-repeat spin" /> Saving…
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2" /> Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountSettingsPage;
